/**
 * Venues.js
 * Snows ProAm — Browse all venues with their amenities and ratings
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faMoon } from "@fortawesome/free-solid-svg-icons";
import logo from "../assets/logo.png";

/* ─────────────────────────────────────────
   THEME — matches BookingPartners exactly
───────────────────────────────────────── */
const DARK = {
  mode:           "dark",
  accent:         "#0C69C8",
  accentDark:     "#0A1D44",
  accentLight:    "#5BA8F7",
  accentGrad:     "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
  accentGlow:     "rgba(12,105,200,0.35)",
  accentBg:       "rgba(12,105,200,0.10)",
  accentBgLight:  "rgba(12,105,200,0.14)",
  accentBorder:   "rgba(12,105,200,0.28)",
  bg:             "#0A0A0A",
  bgCard:         "#141414",
  bgElevated:     "#1E1E1E",
  bgHover:        "#242424",
  border:         "rgba(255,255,255,0.07)",
  borderMd:       "rgba(255,255,255,0.12)",
  borderBright:   "rgba(255,255,255,0.20)",
  text:           "#FAFAFA",
  textSec:        "rgba(250,250,250,0.65)",
  textMuted:      "rgba(250,250,250,0.36)",
  shadow:         "0 4px 20px rgba(0,0,0,0.55)",
  shadowLg:       "0 16px 56px rgba(0,0,0,0.75)",
  headerBg:       "rgba(10,10,10,0.93)",
  gold:           "#FFD700",
  inputBg:        "rgba(255,255,255,0.04)",
  inputBorder:    "rgba(255,255,255,0.10)",
  success:        "#34D399",
  successBg:      "rgba(52,211,153,0.10)",
  danger:         "#EF4444",
  dangerBg:       "rgba(239,68,68,0.10)",
  dangerBorder:   "rgba(239,68,68,0.24)",
};

const LIGHT = {
  mode:           "light",
  accent:         "#0C69C8",
  accentDark:     "#0A1D44",
  accentLight:    "#0C69C8",
  accentGrad:     "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
  accentGlow:     "rgba(12,105,200,0.22)",
  accentBg:       "rgba(12,105,200,0.06)",
  accentBgLight:  "rgba(12,105,200,0.09)",
  accentBorder:   "rgba(12,105,200,0.20)",
  bg:             "#F5F7FA",
  bgCard:         "#FFFFFF",
  bgElevated:     "#EDF0F5",
  bgHover:        "#E4E8F0",
  border:         "rgba(10,29,68,0.08)",
  borderMd:       "rgba(10,29,68,0.13)",
  borderBright:   "rgba(10,29,68,0.22)",
  text:           "#0F1629",
  textSec:        "#3D4A6B",
  textMuted:      "#7A87A8",
  shadow:         "0 4px 20px rgba(10,29,68,0.07)",
  shadowLg:       "0 16px 56px rgba(10,29,68,0.12)",
  headerBg:       "rgba(245,247,250,0.94)",
  gold:           "#C9960A",
  inputBg:        "#F8F9FC",
  inputBorder:    "rgba(10,29,68,0.12)",
  success:        "#059669",
  successBg:      "rgba(5,150,105,0.08)",
  danger:         "#DC2626",
  dangerBg:       "rgba(220,38,38,0.07)",
  dangerBorder:   "rgba(220,38,38,0.20)",
};

function useTheme() {
  const [dark, setDark] = useState(() => {
    try {
      const s = localStorage.getItem("spa-theme");
      if (s) return s !== "light";
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    } catch { return true; }
  });
  const toggle = useCallback(() => {
    setDark(d => {
      const next = !d;
      try { localStorage.setItem("spa-theme", next ? "dark" : "light"); } catch {}
      return next;
    });
  }, []);
  return [dark ? DARK : LIGHT, toggle, dark];
}

const PATHS = {
  star:     "M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z",
  location: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z",
  phone:    "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.19 2.2z",
  mail:     "M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z",
  info:     "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z",
  search:   "M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z",
  arrowRight: "M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z",
  globe:    "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z",
  close:    "M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z",
  check:    "M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z",
  home:     "M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z",
  plus:     "M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z",
};

const Icon = ({ name, size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d={PATHS[name] || PATHS.info} />
  </svg>
);

const Toast = ({ msg, type, visible, C }) => {
  const bg = type === "success" ? C.success : type === "error" ? C.danger : C.accent;
  return (
    <div style={{
      position: "fixed", top: 68, left: "50%",
      transform: `translateX(-50%) translateY(${visible ? "0" : "-80px"})`,
      opacity: visible ? 1 : 0, transition: "all .32s cubic-bezier(.16,1,.3,1)",
      background: bg, color: "#fff", borderRadius: 12,
      display: "flex", alignItems: "center", gap: 9,
      padding: "11px 18px", zIndex: 9999,
      boxShadow: "0 8px 32px rgba(0,0,0,.4)",
      fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", maxWidth: "90vw",
      pointerEvents: "none",
    }}>
      <Icon name={type === "success" ? "check" : type === "error" ? "close" : "info"} size={15} color="#fff" />
      {msg}
    </div>
  );
};

const Venues = () => {
  const navigate = useNavigate();
  const [C, toggleTheme, isDark] = useTheme();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [ratings, setRatings] = useState({});
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [ratingModalForm, setRatingModalForm] = useState({ rating: 0, comment: "", reviewerName: "", isAnonymous: false });
  const [toast, setToast] = useState({ msg: "", type: "success", visible: false });

  const showToast = (msg, type = "success") => {
    setToast({ msg, type, visible: true });
    setTimeout(() => setToast(s => ({ ...s, visible: false })), 3400);
  };

  useEffect(() => {
    fetchUser();
    fetchPartners();
  }, []);

  const fetchUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    console.log("User:", user);
    setUser(user);
    if (user?.id) {
      const { data: profileData } = await supabase.from("profiles").select("name, institute_type, account_type, role, venue_model").eq("id", user.id).maybeSingle();
      console.log("Profile data:", profileData);
      setProfile(profileData);
    }
  };

  const fetchPartners = async () => {
    try {
      // Step 1: Fetch all amenities grouped by business_id
      const { data: amenities, error: amenitiesError } = await supabase
        .from("amenities")
        .select("business_id, id, name, sport_id, price, image_url");

      if (amenitiesError) throw amenitiesError;

      // Group amenities by business_id
      const amenitiesByBusiness = {};
      (amenities || []).forEach(a => {
        if (!amenitiesByBusiness[a.business_id]) {
          amenitiesByBusiness[a.business_id] = [];
        }
        amenitiesByBusiness[a.business_id].push(a);
      });

      // Step 2: Fetch all profiles that have amenities (these are the partners)
      const businessIds = Object.keys(amenitiesByBusiness);
      if (businessIds.length === 0) {
        setPartners([]);
        setLoading(false);
        return;
      }

      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("*")
        // Owner-requested unlisting of the personal/test venue profile.
        .in("id", businessIds)
        .neq("id", "05964b35-7be5-4176-a453-ec9a97496901");

      if (profilesError) throw profilesError;

      // Step 3: Fetch all ratings
      const { data: ratingsData } = await supabase
        .from("partner_ratings")
        .select("*");

      // Calculate averages per partner
      const ratingsMap = {};
      if (ratingsData) {
        ratingsData.forEach(r => {
          if (!ratingsMap[r.partner_id]) {
            ratingsMap[r.partner_id] = { sum: 0, count: 0 };
          }
          ratingsMap[r.partner_id].sum += r.rating;
          ratingsMap[r.partner_id].count += 1;
        });

        Object.keys(ratingsMap).forEach(id => {
          ratingsMap[id] = {
            averageRating: ratingsMap[id].sum / ratingsMap[id].count,
            reviewCount: ratingsMap[id].count
          };
        });
      }

      setRatings(ratingsMap);

      // Step 4: Combine profiles with their amenities
      const partnersWithAmenities = (profiles || []).map(profile => ({
        ...profile,
        amenities: amenitiesByBusiness[profile.id] || [],
        amenityCount: (amenitiesByBusiness[profile.id] || []).length,
      }));

      setPartners(partnersWithAmenities);
    } catch (err) {
      console.error("Error fetching partners:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePartnerClick = (slug) => {
    navigate(`/booking-partners?p=${slug}`);
  };

  // Helper function to extract common prefix from amenity names
  const getCommonPrefix = (names) => {
    if (!names || names.length < 2) return null;
    const sorted = [...names].sort();
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    let prefix = "";
    for (let i = 0; i < Math.min(first.length, last.length); i++) {
      if (first[i] === last[i]) {
        prefix += first[i];
      } else {
        break;
      }
    }
    // Only return prefix if it's meaningful (at least 3 chars and ends with space or common separator)
    if (prefix.length >= 3 && (prefix.endsWith(" ") || prefix.endsWith(":"))) {
      return prefix.trim();
    }
    return null;
  };

  // Get shortened amenity names (remove common prefix)
  const getShortenedAmenityNames = (amenities) => {
    if (!amenities || amenities.length === 0) return [];
    const names = amenities.map(a => a.name || "");
    const commonPrefix = getCommonPrefix(names);
    if (commonPrefix) {
      return amenities.map(a => ({
        ...a,
        displayName: (a.name || "").replace(commonPrefix, "").trim()
      }));
    }
    return amenities.map(a => ({ ...a, displayName: a.name }));
  };

  const openRatingModal = (partner) => {
    setSelectedPartner(partner);
    setRatingModalForm({ rating: 0, comment: "", reviewerName: "", isAnonymous: false });
    setShowAllReviews(false);
    setShowRatingModal(true);
    fetchReviews(partner.id);
  };

  const fetchReviews = async (partnerId) => {
    const { data } = await supabase
      .from("partner_ratings")
      .select("*, profiles(name, profile_img)")
      .eq("partner_id", partnerId)
      .order("created_at", { ascending: false })
      .limit(10);
    setReviews(data || []);
  };

  const submitRating = async () => {
    if (!selectedPartner) return;
    if (ratingModalForm.rating < 1 || ratingModalForm.rating > 5) {
      showToast("Please select a rating between 1 and 5", "error");
      return;
    }
    if (!ratingModalForm.isAnonymous && !ratingModalForm.reviewerName?.trim()) {
      showToast("Please enter your name or choose to post anonymously", "error");
      return;
    }

    setSubmitting(true);

    const reviewerName = ratingModalForm.isAnonymous
      ? "Anonymous"
      : (ratingModalForm.reviewerName?.trim() || "Anonymous");

    // For authenticated users, check for existing rating
    if (user) {
      const { data: existing } = await supabase
        .from("partner_ratings")
        .select("*")
        .eq("partner_id", selectedPartner.id)
        .eq("user_id", user.id)
        .single();

      let error;
      if (existing) {
        const { error: updateError } = await supabase
          .from("partner_ratings")
          .update({
            rating: ratingModalForm.rating,
            comment: ratingModalForm.comment,
            reviewer_name: reviewerName
          })
          .eq("id", existing.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from("partner_ratings")
          .insert([{
            partner_id: selectedPartner.id,
            user_id: user.id,
            rating: ratingModalForm.rating,
            comment: ratingModalForm.comment,
            reviewer_name: reviewerName,
          }]);
        error = insertError;
      }

      setSubmitting(false);
      if (error) {
        showToast("Failed to submit review. Please try again.", "error");
        return;
      }
    } else {
      // Non-authenticated user - just insert with reviewer name
      const { error } = await supabase
        .from("partner_ratings")
        .insert([{
          partner_id: selectedPartner.id,
          user_id: null,
          rating: ratingModalForm.rating,
          comment: ratingModalForm.comment,
          reviewer_name: reviewerName,
        }]);

      setSubmitting(false);
      if (error) {
        showToast("Failed to submit review. Please try again.", "error");
        return;
      }
    }

    // Refresh ratings and close modal
    fetchPartners();
    setShowRatingModal(false);
    setSelectedPartner(null);
    showToast("Thank you for your review!", "success");
  };

  const filtered = partners.filter(p => {
    const q = search.toLowerCase();
    if (!q) return true;
    const amenitySports = p.amenities?.map(a => a.sport_id?.toLowerCase() || "") || [];
    return (
      p.name?.toLowerCase().includes(q) ||
      p.address?.toLowerCase().includes(q) ||
      p.bio?.toLowerCase().includes(q) ||
      amenitySports.some(sport => sport.includes(q))
    );
  });

  return (
    <div style={{
      minHeight: "100vh",
      background: C.bg,
      color: C.text,
      fontFamily: "'DM Sans', sans-serif",
    }}>
      {/* Header */}
      <header style={{
        position: "sticky",
        top: 0,
        background: C.headerBg,
        backdropFilter: "blur(20px) saturate(1.2)",
        WebkitBackdropFilter: "blur(20px) saturate(1.2)",
        borderBottom: `1px solid ${C.border}`,
        padding: "0 16px",
        height: 56,
        zIndex: 100,
        display: "flex",
        alignItems: "center",
      }}>
        <style>{`
          @media (max-width: 768px) {
            .hide-mobile { display: none !important; }
            .header-logo-text { font-size: 12px !important; }
            .header-title { font-size: 13px !important; }
          }
        `}</style>
        <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button onClick={() => navigate("/")} style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: C.bgElevated, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img src={logo} alt="Snows ProAm" style={{ width: 18, height: 18, objectFit: "contain" }} />
            </div>
            <span style={{ fontFamily: "'Sora', sans-serif", fontSize: 14, fontWeight: 700, color: C.text }}>
              Snows ProAm <span style={{ color: C.accent }}>Venues</span>
            </span>
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, background: C.accentBg, border: `1px solid ${C.accentBorder}`, fontSize: 10, fontWeight: 700, color: C.accent, letterSpacing: 0.4 }}>
            DIRECTORY
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              title={isDark ? "Light mode" : "Dark mode"}
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: C.bgElevated,
                border: `1px solid ${C.border}`,
                color: C.text,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "transform 0.2s ease",
              }}
            >
              <FontAwesomeIcon icon={isDark ? faSun : faMoon} style={{ fontSize: 12 }} />
            </button>
            <button onClick={() => navigate(-1)} style={{ fontSize: 12, fontWeight: 500, color: C.textSec, padding: "5px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.bgElevated, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
              ← Back
            </button>
          </div>
        </div>
      </header>

      {/* Search Bar */}
      <div style={{
        maxWidth: 1200,
        margin: "0 auto",
        padding: "20px 16px",
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 14,
          flexWrap: "wrap",
        }}>
          <div>
            <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: 28, fontWeight: 900, color: C.text, margin: 0 }}>Venues</h1>
            <p style={{ fontSize: 13, color: C.textMuted, margin: "4px 0 0" }}>Search Snows ProAm venues by name, location, or sport.</p>
          </div>
          <button
            className="btn-primary"
            onClick={() => navigate("/venues/join")}
            style={{ padding: "10px 16px", fontSize: 13, borderRadius: 10 }}
          >
            <Icon name="plus" size={13} color="#fff" />
            {user && (profile?.role === "partner" || profile?.venue_model || profile?.account_type === "business" || profile?.account_type === "institute") ? `Add to ${profile?.name || "your venue"}` : "Add Your Venue"}
          </button>
        </div>
        <div style={{
          display: "flex",
          alignItems: "center",
          background: C.inputBg,
          border: `1.5px solid ${C.inputBorder}`,
          borderRadius: 12,
          padding: "12px 16px",
          gap: 10,
        }}>
          <Icon name="search" size={18} color={C.textMuted} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, location, or sport..."
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: C.text,
              fontSize: 14,
              fontWeight: 500,
            }}
          />
        </div>
        <p style={{ fontSize: 12, color: C.textMuted, marginTop: 8 }}>
          {partners.length} venue{partners.length !== 1 ? "s" : ""} found
        </p>
      </div>

      {/* Venues Grid */}
      <div style={{
        maxWidth: 1200,
        margin: "0 auto",
        padding: "0 16px 60px",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(min(280px, 100%), 1fr))",
        gap: 20,
      }}>
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{
              background: C.bgCard,
              borderRadius: 16,
              overflow: "hidden",
              border: `1px solid ${C.border}`,
              minHeight: 320,
              animation: "pulse 1.5s ease-in-out infinite",
            }} />
          ))
        ) : filtered.length === 0 ? (
          <div style={{
            gridColumn: "1 / -1",
            textAlign: "center",
            padding: 60,
            color: C.textMuted,
          }}>
            <Icon name="search" size={48} color={C.textMuted} style={{ opacity: 0.3, marginBottom: 16 }} />
            <p style={{ fontSize: 16, margin: 0 }}>No venues found</p>
            <p style={{ fontSize: 13, marginTop: 8 }}>Try adjusting your search</p>
          </div>
        ) : (
          filtered.map((partner, i) => {
            const ratingStats = ratings[partner.id] || { averageRating: 0, reviewCount: 0 };
            const displayRating = ratingStats.averageRating > 0
              ? ratingStats.averageRating.toFixed(1)
              : null;
            const reviewCount = ratingStats.reviewCount || 0;
            return (
              <div
                key={partner.id}
                className="fu"
                onClick={() => handlePartnerClick(partner.slug || partner.id)}
                style={{ animationDelay: `${i * 0.05}s`, cursor: "pointer" }}
              >
                <div
                  style={{
                    background: C.bgCard,
                    borderRadius: 16,
                    overflow: "hidden",
                    border: `1px solid ${C.border}`,
                    transition: "all 0.2s ease",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = C.shadowLg;
                    e.currentTarget.style.borderColor = C.accentBorder;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.borderColor = C.border;
                  }}
                >
                  {/* Cover Image */}
                  <div style={{
                    height: 160,
                    background: partner.cover_img
                      ? `url(${partner.cover_img}) center/cover`
                      : C.accentGrad,
                    position: "relative",
                  }}>
                    {/* Rating Badge */}
                    {displayRating && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          openRatingModal(partner);
                        }}
                        style={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          background: "rgba(0,0,0,0.75)",
                          backdropFilter: "blur(8px)",
                          borderRadius: 20,
                          padding: "6px 10px",
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          cursor: "pointer",
                          transition: "transform 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.08)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        <Icon name="star" size={14} color={C.gold} />
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{displayRating}</span>
                        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>({reviewCount})</span>
                      </div>
                    )}
                  </div>

                  {/* Profile Image */}
                  <div style={{
                    width: 64,
                    height: 64,
                    borderRadius: 16,
                    overflow: "hidden",
                    border: `3px solid ${C.bgCard}`,
                    background: C.bgElevated,
                    margin: "-32px 16px 12px",
                    position: "relative",
                    zIndex: 1,
                  }}>
                    {partner.profile_img ? (
                      <img src={partner.profile_img} alt={partner.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: C.accentBg }}>
                        <Icon name="info" size={28} color={C.accent} />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div style={{ padding: "0 16px 16px", flex: 1, display: "flex", flexDirection: "column" }}>
                    <h3 style={{
                      fontFamily: "'Sora', sans-serif",
                      fontSize: 18,
                      fontWeight: 800,
                      color: C.text,
                      letterSpacing: -0.3,
                      margin: "0 0 6px",
                    }}>
                      {partner.name || "Venue"}
                    </h3>

                    {partner.address && (
                      <div style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 5,
                        fontSize: 12,
                        color: C.textSec,
                        marginBottom: 8,
                        minHeight: 32,
                      }}>
                        <Icon name="location" size={12} color={C.textMuted} style={{ flexShrink: 0, marginTop: 2 }} />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", lineHeight: 1.4 }}>
                          {partner.address}
                        </span>
                      </div>
                    )}

                    {/* Amenity Count */}
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 10,
                      flexWrap: "wrap",
                    }}>
                      <div style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: C.textSec,
                        background: C.accentBg,
                        padding: "4px 8px",
                        borderRadius: 6,
                      }}>
                        {partner.amenityCount} amenity{partner.amenityCount !== 1 ? "ies" : ""} available
                      </div>
                    </div>

                    {partner.bio && (
                      <p style={{
                        fontSize: 13,
                        color: C.textSec,
                        margin: "0 0 12px",
                        lineHeight: 1.5,
                        display: "-webkit-box",
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}>
                        {partner.bio}
                      </p>
                    )}

                    {/* Amenities Preview */}
                    {partner.amenities && partner.amenities.length > 0 && (
                      <div style={{
                        marginTop: "auto",
                        paddingTop: 12,
                        borderTop: `1px solid ${C.border}`,
                      }}>
                        <p style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, marginBottom: 6 }}>
                          OFFERINGS
                        </p>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {getShortenedAmenityNames(partner.amenities).slice(0, 4).map((a) => (
                            <div
                              key={a.id}
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                color: C.textSec,
                                background: C.bgElevated,
                                padding: "4px 7px",
                                borderRadius: 6,
                                border: `1px solid ${C.border}`,
                              }}
                            >
                              {a.displayName?.length > 18 ? a.displayName.slice(0, 18) + "..." : a.displayName}
                            </div>
                          ))}
                          {partner.amenities.length > 4 && (
                            <div style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: C.textMuted,
                              padding: "4px 7px",
                            }}>
                              +{partner.amenities.length - 4} more
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* CTA Buttons */}
                    <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePartnerClick(partner.slug || partner.id);
                        }}
                        style={{
                          flex: 1,
                          padding: "11px 16px",
                          background: C.accentGrad,
                          border: "none",
                          borderRadius: 10,
                          color: "#fff",
                          fontSize: 13,
                          fontWeight: 700,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                          transition: "all 0.15s",
                        }}
                      >
                        View & Book
                        <Icon name="arrowRight" size={14} color="#fff" />
                      </button>
                   
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openRatingModal(partner);
                        }}
                        style={{
                          padding: "11px 14px",
                          background: C.bgElevated,
                          border: `1px solid ${C.border}`,
                          borderRadius: 10,
                          color: C.text,
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                          transition: "all 0.15s",
                        }}
                      >
                        <Icon name="star" size={14} color={C.gold} />
                        Review
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Rating Modal */}
      {showRatingModal && selectedPartner && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowRatingModal(false);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.72)",
            backdropFilter: "blur(8px)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "fadeIn 0.2s ease",
          }}
        >
          <div
            className="modal-sheet"
            style={{
              background: C.bgCard,
              border: `1px solid ${C.borderMd}`,
              borderRadius: 20,
              width: "100%",
              maxWidth: 520,
              maxHeight: "90vh",
              overflowY: "auto",
              animation: "slideUp 0.28s cubic-bezier(.16,1,.3,1)",
            }}
          >
            {/* Header */}
            <div style={{
              padding: "20px 24px",
              borderBottom: `1px solid ${C.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
              <div>
                <h3 style={{
                  fontFamily: "'Sora', sans-serif",
                  fontSize: 18,
                  fontWeight: 800,
                  color: C.text,
                  margin: 0,
                }}>
                  Leave a Review
                </h3>
                <p style={{ fontSize: 13, color: C.textSec, margin: "4px 0 0" }}>
                  {selectedPartner.name}
                </p>
              </div>
              <button
                onClick={() => setShowRatingModal(false)}
                aria-label="Close review"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: C.bgElevated,
                  border: `1px solid ${C.border}`,
                  color: C.text,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon name="close" size={16} />
              </button>
            </div>

            {/* Rating Stars */}
            <div style={{ padding: "24px 24px 16px" }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: C.textSec, marginBottom: 12, display: "block" }}>
                Your Rating
              </label>
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    aria-label={`${star} ${star === 1 ? "star" : "stars"}`}
                    aria-pressed={ratingModalForm.rating === star}
                    onClick={() => setRatingModalForm({ ...ratingModalForm, rating: star })}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      transition: "transform 0.15s",
                      transform: ratingModalForm.rating >= star ? "scale(1.1)" : "scale(1)",
                    }}
                  >
                    <svg width={36} height={36} viewBox="0 0 24 24" fill={ratingModalForm.rating >= star ? C.gold : C.borderMd}>
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                  </button>
                ))}
              </div>
              {ratingModalForm.rating > 0 && (
                <div style={{ textAlign: "center", marginTop: 8, fontSize: 12, color: C.textSec }}>
                  {["Very Poor", "Poor", "Average", "Good", "Excellent"][ratingModalForm.rating - 1]}
                </div>
              )}
            </div>

            {/* Name Input */}
            <div style={{ padding: "0 24px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: C.textSec, display: "block" }}>
                  Your Name
                </label>
                <label style={{ fontSize: 11, color: C.textMuted, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={ratingModalForm.isAnonymous}
                    onChange={(e) => setRatingModalForm({ ...ratingModalForm, isAnonymous: e.target.checked })}
                    style={{ width: 14, height: 14, cursor: "pointer" }}
                  />
                  Post anonymously
                </label>
              </div>
              <input
                type="text"
                value={ratingModalForm.reviewerName}
                onChange={(e) => setRatingModalForm({ ...ratingModalForm, reviewerName: e.target.value })}
                placeholder="Enter your name"
                disabled={ratingModalForm.isAnonymous}
                style={{
                  width: "100%",
                  padding: 12,
                  background: ratingModalForm.isAnonymous ? C.bgElevated : C.inputBg,
                  border: `1px solid ${ratingModalForm.isAnonymous ? C.border : C.inputBorder}`,
                  borderRadius: 10,
                  color: ratingModalForm.isAnonymous ? C.textMuted : C.text,
                  fontSize: 14,
                  fontFamily: "'DM Sans', sans-serif",
                  outline: "none",
                }}
              />
            </div>

            {/* Comment */}
            <div style={{ padding: "0 24px 16px" }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: C.textSec, marginBottom: 8, display: "block" }}>
                Your Review (optional)
              </label>
              <textarea
                value={ratingModalForm.comment}
                onChange={(e) => setRatingModalForm({ ...ratingModalForm, comment: e.target.value })}
                placeholder="Share your experience with this venue..."
                rows={4}
                style={{
                  width: "100%",
                  padding: 12,
                  background: C.inputBg,
                  border: `1px solid ${C.inputBorder}`,
                  borderRadius: 10,
                  color: C.text,
                  fontSize: 14,
                  fontFamily: "'DM Sans', sans-serif",
                  resize: "none",
                  outline: "none",
                }}
              />
            </div>

            {/* Existing Reviews */}
            {reviews.length > 0 && (
              <div style={{ padding: "0 24px 16px", borderTop: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: C.textSec }}>
                    Reviews ({reviews.length})
                  </label>
                  {reviews.length > 3 && (
                    <button
                      onClick={() => setShowAllReviews(!showAllReviews)}
                      style={{
                        background: "none",
                        border: "none",
                        color: C.accent,
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      {showAllReviews ? "Show Less" : `View All (${reviews.length})`}
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: showAllReviews ? "none" : 200, overflowY: "auto" }}>
                  {(showAllReviews ? reviews : reviews.slice(0, 3)).map((review) => {
                    const displayName = review.reviewer_name || review.profiles?.name || "Anonymous";
                    const initials = (displayName || "A")[0];
                    return (
                      <div
                        key={review.id}
                        style={{
                          padding: 12,
                          background: C.bgElevated,
                          borderRadius: 10,
                          border: `1px solid ${C.border}`,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                          {review.profiles?.profile_img ? (
                            <img
                              src={review.profiles.profile_img}
                              alt={displayName}
                              style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                background: C.accentBg,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <span style={{ fontSize: 12, fontWeight: 700, color: C.accent }}>
                                {initials}
                              </span>
                            </div>
                          )}
                          <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                            {displayName}
                          </span>
                          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 3 }}>
                            <Icon name="star" size={11} color={C.gold} />
                            <span style={{ fontSize: 12, fontWeight: 700, color: C.gold }}>{review.rating}</span>
                          </div>
                        </div>
                        {review.comment && (
                          <p style={{ fontSize: 12, color: C.textSec, margin: 0, lineHeight: 1.4 }}>
                            {review.comment}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div style={{
              padding: "16px 24px 24px",
              borderTop: `1px solid ${C.border}`,
              display: "flex",
              gap: 10,
            }}>
              <button
                onClick={() => setShowRatingModal(false)}
                style={{
                  flex: 1,
                  padding: "12px 16px",
                  background: C.bgElevated,
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  color: C.text,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitRating}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: "12px 16px",
                  background: C.accentGrad,
                  border: "none",
                  borderRadius: 10,
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: submitting ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.7 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: 14,
                      height: 14,
                      border: "2px solid rgba(255,255,255,0.3)",
                      borderTopColor: "#fff",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }} />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Icon name="check" size={14} color="#fff" />
                    Submit Review
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800;900&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .fu { animation: fadeUp 0.55s cubic-bezier(.16,1,.3,1) both; }

        .btn-ghost {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          background: transparent;
          color: ${C.textSec};
          border: 1px solid ${C.border};
          border-radius: 10px;
          padding: 10px 18px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
        }
        .btn-ghost:hover {
          background: ${C.bgHover};
          border-color: ${C.borderMd};
          color: ${C.text};
        }
        .theme-btn {
          background: transparent;
          border: 1px solid ${C.border};
          transition: all 0.15s;
        }
        .theme-btn:hover {
          background: ${C.bgHover};
          border-color: ${C.borderMd};
        }
      `}</style>

      {/* Toast Notification */}
      <Toast {...toast} C={C} />
    </div>
  );
};

export default Venues;
